import React, { useState, useEffect, useRef } from 'react';
import { LayoutDashboard, Users, Trello, FileText, PenTool, Euro, PieChart, Shield, ChevronDown, LogOut, Building, Mail, Zap, Key, ListChecks, Brain, Facebook, BarChart3, FileCheck, Settings, MessageSquare, Calculator, Activity, UserCheck, UserCog, Search, Receipt } from 'lucide-react';
import { Agency, UserProfile } from '../types';
import { storage } from '../services/storage';
import { canAccessBilling } from '../services/billingService';
import { impersonationService } from '../services/impersonationService';
import { useModal } from './ui/ModalProvider';

interface SidebarProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  userProfile: UserProfile | null;
  availableAgencies: Agency[];
  selectedAgencyId: string | null;
  setSelectedAgencyId: (id: string | null) => void;
  onLogout: () => void;
  metaEnabled?: boolean; // Feature flag for Meta
}

export const Sidebar: React.FC<SidebarProps> = ({ 
    activeTab, 
    setActiveTab, 
    userProfile, 
    availableAgencies, 
    selectedAgencyId, 
    setSelectedAgencyId,
    onLogout,
    metaEnabled = false
}) => {
  const { toast } = useModal();
  const [financeEnabled, setFinanceEnabled] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonationUsers, setImpersonationUsers] = useState<UserProfile[]>([]);
  const [impersonationSearch, setImpersonationSearch] = useState('');
  const [selectedImpersonationUser, setSelectedImpersonationUser] = useState<UserProfile | null>(null);
  const [showImpersonationDropdown, setShowImpersonationDropdown] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const impersonationDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkFeatureFlags = async () => {
      const [finance, billingConfig] = await Promise.all([
        storage.isFinanceEnabled(),
        storage.getBillingConfig()
      ]);
      setFinanceEnabled(finance);
      setBillingEnabled(canAccessBilling(billingConfig, userProfile, selectedAgencyId, availableAgencies));
    };
    if (userProfile) {
      checkFeatureFlags();
    }
  }, [userProfile, selectedAgencyId, availableAgencies]);

  // Vérifier si on est en mode impersonation
  useEffect(() => {
    setIsImpersonating(impersonationService.isImpersonating());
  }, [userProfile]);

  // Charger la liste des utilisateurs pour l'impersonation (superadmin seulement)
  useEffect(() => {
    const loadUsers = async () => {
      if (userProfile?.role === 'superadmin' && !isImpersonating) {
        try {
          const profiles = await storage.getAllProfiles();
          if (profiles && Array.isArray(profiles)) {
            // Exclure le superadmin lui-même de la liste
            setImpersonationUsers(profiles.filter(u => u.id !== userProfile.id));
          }
        } catch (error) {
          console.error('Error loading users for impersonation:', error);
        }
      }
    };
    loadUsers();
  }, [userProfile, isImpersonating]);

  // Fermer le dropdown quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (impersonationDropdownRef.current && !impersonationDropdownRef.current.contains(event.target as Node)) {
        setShowImpersonationDropdown(false);
      }
    };

    if (showImpersonationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showImpersonationDropdown]);
  
  const allMenuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'crm', label: 'Contacts & Biens', icon: Users },
    { id: 'pipeline', label: 'Pipeline', icon: Trello },
    { id: 'proposal', label: 'Propositions', icon: FileText },
    { id: 'contract', label: 'Contrats', icon: PenTool },
    // New Item
    { id: 'managed', label: 'Biens en gestion', icon: Key },
    { id: 'expenses', label: 'Finances', icon: Euro },
    { id: 'billing', label: 'Facturation', icon: Receipt },
    { id: 'featurebase', label: 'Feedback', icon: MessageSquare },
  ];

  // Filtrer les items selon les feature flags et le rôle
  const menuItems = allMenuItems.filter(item => {
    // Masquer le tableau de bord pour les prestataires marketing
    if (item.id === 'dashboard' && userProfile?.role === 'prestataire_marketing') {
      return false;
    }
    // Masquer les finances si le feature flag n'est pas activé
    if (item.id === 'expenses' && !financeEnabled) {
      return false;
    }
    // Masquer la facturation si le feature flag n'est pas activé
    if (item.id === 'billing' && !billingEnabled) {
      return false;
    }
    return true;
  });

  const futureItems = [
    { id: 'owner-dash', label: 'Espace Propriétaire', icon: PieChart, badge: 'Bientôt' },
  ];

  const displayName = userProfile?.firstName 
    ? `${userProfile.firstName} ${userProfile.lastName?.charAt(0)}.` 
    : userProfile?.email?.split('@')[0];

  // Handler pour l'impersonation
  const handleImpersonate = async () => {
    if (!selectedImpersonationUser) {
      toast.error('Veuillez sélectionner un utilisateur');
      return;
    }

    setImpersonating(true);
    try {
      const { success, error } = await impersonationService.impersonateUser(selectedImpersonationUser.id);
      
      if (!success) {
        toast.error(error || 'Erreur lors de l\'impersonation');
        return;
      }

      toast.success('Impersonation démarrée');
      // Recharger la page pour appliquer la nouvelle session
      window.location.reload();
    } catch (error: any) {
      toast.error('Erreur lors de l\'impersonation: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setImpersonating(false);
    }
  };

  // Filtrer les utilisateurs selon la recherche
  const filteredImpersonationUsers = impersonationUsers.filter(user => {
    if (!impersonationSearch) return true;
    const searchLower = impersonationSearch.toLowerCase();
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
    const email = (user.email || '').toLowerCase();
    return fullName.includes(searchLower) || email.includes(searchLower);
  });

  // Handler pour sélectionner un utilisateur depuis le dropdown
  const handleSelectUser = (user: UserProfile) => {
    setSelectedImpersonationUser(user);
    setImpersonationSearch(`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email);
    setShowImpersonationDropdown(false);
  };

  // Handler pour le changement de recherche
  const handleSearchChange = (value: string) => {
    setImpersonationSearch(value);
    setShowImpersonationDropdown(true);
    if (!value) {
      setSelectedImpersonationUser(null);
    }
  };

  return (
    <div className="w-64 bg-slate-900 text-white h-screen fixed left-0 top-0 flex flex-col shadow-xl no-print z-50">
      {/* HEADER */}
      <div className="p-6 flex flex-col items-center border-b border-slate-800 shrink-0">
        <div className="flex flex-col items-center mb-6 w-full">
            <div className="p-2 rounded mb-2 w-full flex justify-center">
                 <img 
                    src="https://kfrfvqzjxnadselwusci.supabase.co/storage/v1/object/public/app-assets/logo.png" 
                    alt="PLACID" 
                    className="h-8 w-auto object-contain"
                 />
            </div>
            <p className="text-xs text-slate-400 uppercase tracking-widest mt-1">Manager</p>
        </div>

        {/* Agency Selector - Masqué pour les prestataires marketing */}
        {userProfile?.role !== 'prestataire_marketing' && (
          <div className="w-full relative">
              <label className="text-[10px] text-slate-500 uppercase font-bold mb-1 block">Agence Active</label>
              <div className="relative">
                  <select
                      value={selectedAgencyId || ''}
                      onChange={(e) => setSelectedAgencyId(e.target.value === '' ? null : e.target.value)}
                      className="sidebar-agency-select w-full bg-slate-800 text-white text-sm rounded border border-slate-700 py-2 pl-3 pr-8 appearance-none focus:outline-none focus:border-amber-500 truncate"
                  >
                      {userProfile?.accessLevel === 'national' && (
                          <option value="">🌍 Vue Globale (Toutes)</option>
                      )}
                      {availableAgencies.map(agency => (
                          <option key={agency.id} value={agency.id}>{agency.city} ({agency.name})</option>
                      ))}
                      {availableAgencies.length === 0 && userProfile?.accessLevel !== 'national' && (
                           <option value="" disabled>Aucune agence assignée</option>
                      )}
                  </select>
                  <ChevronDown className="absolute right-2 top-2.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
          </div>
        )}
      </div>

      {/* NAVIGATION - SCROLLABLE */}
      <nav className="flex-1 overflow-y-auto py-4 min-h-0">
        {/* Masquer la section Gestion pour les prestataires marketing (setters et superviseurs) */}
        {userProfile?.role !== 'prestataire_marketing' && (
          <>
            <div className="px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Gestion
            </div>
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                        activeTab === item.id
                          ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                          : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3 shrink-0" />
                      {item.label}
                    </button>
                  </li>
                );
              })}
              <li>
                <button
                  onClick={() => setActiveTab('estimation')}
                  className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'estimation'
                      ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Calculator className="w-5 h-5 mr-3 shrink-0" />
                  Estimation
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('management_settings')}
                  className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                    activeTab === 'management_settings'
                      ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                  }`}
                >
                  <Settings className="w-5 h-5 mr-3 shrink-0" />
                  Paramètres
                </button>
              </li>
            </ul>
          </>
        )}

        {(userProfile?.role === 'admin' || userProfile?.role === 'superadmin') && (
            <>
                <div className="mt-6 px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Administration
                </div>
                <ul className="space-y-1">
                    <li>
                        <button
                        onClick={() => setActiveTab('admin_users')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'admin_users'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <Shield className="w-5 h-5 mr-3 shrink-0" />
                        Utilisateurs
                        </button>
                    </li>
                    <li>
                        <button
                        onClick={() => setActiveTab('admin_agencies')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'admin_agencies'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <Building className="w-5 h-5 mr-3 shrink-0" />
                        Agences
                        </button>
                    </li>
                    <li>
                        <button
                        onClick={() => setActiveTab('admin_sequences')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'admin_sequences'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <Zap className="w-5 h-5 mr-3 shrink-0" />
                        Séquences
                        </button>
                    </li>
                    <li>
                        <button
                        onClick={() => setActiveTab('admin_documents')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'admin_documents'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <FileText className="w-5 h-5 mr-3 shrink-0" />
                        Types Docs
                        </button>
                    </li>
                    <li>
                        <button
                        onClick={() => setActiveTab('admin_checklists')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'admin_checklists'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <ListChecks className="w-5 h-5 mr-3 shrink-0" />
                        Checklists
                        </button>
                    </li>
                    <li>
                        <button
                        onClick={() => setActiveTab('admin_marketing')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'admin_marketing'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <Mail className="w-5 h-5 mr-3 shrink-0" />
                        Marketing
                        </button>
                    </li>
                    {userProfile?.role === 'superadmin' && (
                        <>
                            <li>
                                <button
                                onClick={() => setActiveTab('admin_ai_consumption')}
                                className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                    activeTab === 'admin_ai_consumption'
                                    ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                }`}
                                >
                                <Brain className="w-5 h-5 mr-3 shrink-0" />
                                Conso IA
                                </button>
                            </li>
                            <li>
                                <button
                                onClick={() => setActiveTab('admin_audit_logs')}
                                className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                    activeTab === 'admin_audit_logs'
                                    ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                }`}
                                >
                                <FileCheck className="w-5 h-5 mr-3 shrink-0" />
                                Logs d'Audit
                                </button>
                            </li>
                            <li>
                                <button
                                onClick={() => setActiveTab('admin_settings')}
                                className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                    activeTab === 'admin_settings'
                                    ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                }`}
                                >
                                <Settings className="w-5 h-5 mr-3 shrink-0" />
                                Paramètres
                                </button>
                            </li>
                        </>
                    )}
                    {metaEnabled && (
                        <>
                            <li>
                                <button
                                onClick={() => setActiveTab('admin_meta')}
                                className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                    activeTab === 'admin_meta'
                                    ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                }`}
                                >
                                <Facebook className="w-5 h-5 mr-3 shrink-0" />
                                Intégration Meta
                                </button>
                            </li>
                        </>
                    )}
                </ul>
            </>
        )}


        {(userProfile?.role === 'prestataire_marketing' || userProfile?.role === 'admin' || userProfile?.role === 'superadmin') && (
            <>
                <div className="mt-6 px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Marketing
                </div>
                <ul className="space-y-1">
                    {userProfile?.role === 'prestataire_marketing' && (
                        <li>
                            <button
                            onClick={() => setActiveTab('dashboard')}
                            className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                activeTab === 'dashboard'
                                ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                            }`}
                            >
                            <LayoutDashboard className="w-5 h-5 mr-3 shrink-0" />
                            Tableau de bord
                            </button>
                        </li>
                    )}
                    {metaEnabled && (
                        <>
                            <li>
                                <button
                                onClick={() => setActiveTab('marketing_leads')}
                                className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                    activeTab === 'marketing_leads'
                                    ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                }`}
                                >
                                <UserCheck className="w-5 h-5 mr-3 shrink-0" />
                                Gestion Leads
                                </button>
                            </li>
                            {(userProfile?.role === 'admin' || userProfile?.role === 'superadmin' || (userProfile?.role === 'prestataire_marketing' && userProfile?.isSupervisor)) && (
                                <li>
                                    <button
                                    onClick={() => setActiveTab('marketing_stats')}
                                    className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                                        activeTab === 'marketing_stats'
                                        ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                                    }`}
                                    >
                                    <BarChart3 className="w-5 h-5 mr-3 shrink-0" />
                                    Stats
                                    </button>
                                </li>
                            )}
                        </>
                    )}
                    <li>
                        <button
                        onClick={() => setActiveTab('estimation')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'estimation'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <Calculator className="w-5 h-5 mr-3 shrink-0" />
                        Estimation
                        </button>
                    </li>
                </ul>
            </>
        )}

        {(userProfile?.role === 'admin' || userProfile?.role === 'superadmin') && (
            <>
                <div className="mt-6 px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Statistiques
                </div>
                <ul className="space-y-1">
                    <li>
                        <button
                        onClick={() => setActiveTab('estimation_stats')}
                        className={`w-full flex items-center px-6 py-3 text-sm font-medium transition-colors duration-150 ${
                            activeTab === 'estimation_stats'
                            ? 'bg-slate-800 text-amber-500 border-r-4 border-amber-500'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                        >
                        <Activity className="w-5 h-5 mr-3 shrink-0" />
                        Stats Estimateur
                        </button>
                    </li>
                </ul>
            </>
        )}

        {/* Masquer la section "À venir" pour les prestataires marketing (setters et superviseurs) */}
        {userProfile?.role !== 'prestataire_marketing' && (
          <>
            <div className="mt-8 px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              À venir
            </div>
            <ul className="space-y-1 opacity-70">
              {futureItems.map((item) => {
                const Icon = item.icon;
                return (
                  <li key={item.id}>
                    <button
                      disabled
                      className="w-full flex items-center justify-between px-6 py-3 text-sm font-medium text-slate-400 cursor-not-allowed"
                    >
                      <div className="flex items-center">
                        <Icon className="w-5 h-5 mr-3 shrink-0" />
                        {item.label}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        {/* Section Impersonation - Superadmin seulement - Tout en bas */}
        {userProfile?.role === 'superadmin' && !isImpersonating && (
          <>
            <div className="mt-8 px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Impersonation
            </div>
            <div className="px-4 space-y-2 pb-4">
              <div className="relative" ref={impersonationDropdownRef}>
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={impersonationSearch}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => setShowImpersonationDropdown(true)}
                  className="w-full bg-slate-800 text-white text-sm rounded-lg border border-slate-700 py-2.5 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-all"
                />
                {showImpersonationDropdown && filteredImpersonationUsers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-700 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                    {filteredImpersonationUsers.map(user => {
                      const displayName = user.firstName && user.lastName 
                        ? `${user.firstName} ${user.lastName}`
                        : user.email;
                      return (
                        <button
                          key={user.id}
                          onClick={() => handleSelectUser(user)}
                          className="w-full text-left px-4 py-2.5 hover:bg-slate-700 transition-colors flex items-center gap-3 border-b border-slate-700 last:border-b-0"
                        >
                          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-semibold text-xs shrink-0">
                            {user.firstName?.[0] || user.email[0].toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">
                              {displayName}
                            </p>
                            <p className="text-xs text-slate-400 truncate">
                              {user.email}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {selectedImpersonationUser && (
                <button
                  onClick={handleImpersonate}
                  disabled={impersonating}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-2.5 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-amber-600/20"
                >
                  {impersonating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Impersonation...</span>
                    </>
                  ) : (
                    <>
                      <UserCog className="w-4 h-4" />
                      <span>Impersonner {selectedImpersonationUser.firstName || selectedImpersonationUser.email.split('@')[0]}</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </nav>

      {/* FOOTER USER INFO & LOGOUT */}
      <div className="p-4 border-t border-slate-800 bg-slate-900 shrink-0 space-y-4">
        <button 
            onClick={onLogout}
            className="w-full flex items-center px-2 py-2 text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
        >
            <LogOut className="w-5 h-5 mr-3" />
            Déconnexion
        </button>

        <div className="flex items-center pt-2 border-t border-slate-800">
            <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-slate-900 font-bold shrink-0">
                {userProfile?.firstName?.[0] || userProfile?.email?.[0].toUpperCase() || 'U'}
            </div>
            <div className="ml-3 overflow-hidden">
                <p className="text-sm font-medium text-white truncate capitalize">
                    {displayName}
                </p>
                <p className="text-xs text-slate-400 truncate flex items-center">
                    {userProfile?.role === 'superadmin' ? 'Super Admin' : 
                     userProfile?.role === 'admin' ? 'Admin' : 
                     userProfile?.role === 'prestataire_marketing' && userProfile?.isSupervisor ? 'Superviseur marketing' :
                     userProfile?.role === 'prestataire_marketing' && !userProfile?.isSupervisor ? 'Prestataire marketing' :
                     'Utilisateur'} 
                    {userProfile?.role !== 'prestataire_marketing' && (
                      <>
                        <span className="mx-1">•</span>
                        {userProfile?.accessLevel === 'national' ? 'National' : 'Agence'}
                      </>
                    )}
                </p>
            </div>
        </div>
      </div>
    </div>
  );
};